/**
 * 慢启动检测（启动耗时 > 8s 提示）
 *
 * 原理（零资源设计）：启动链路已有精确的就绪时刻（Suji/CRDT 页面骨架屏退出
 * = 增量下载完成 + 用户可用），只在那一刻读一次 performance.now()（相对
 * timeOrigin=webview 导航起点，涵盖加载/解析/渲染/同步全程）并比较阈值。
 * 快速启动全程仅多一次时间戳读取 + 一次比较：无定时器、无监听、无轮询；
 * 慢启动路径才有唯一一个 1.5s 延迟弹出定时器（让编辑器首帧/启动聚焦先完成）。
 *
 * 守卫：
 * - 耗时 > 8000ms（正常基线 ~3s）
 * - ★ 仅增量同步路径（markStartupSyncMode 标记）：全量下载（新装 / 换设备 /
 *   手动全量重下）需完整拉取云端数据，耗时长属预期，提示反而误导；增量路径
 *   本应有本地缓存（基线 ~3s），超阈值才值得提示
 * - 仅云端对齐路径（离线只读兜底放行时增量下载未完成，提示语义不成立）
 * - 非 DEV（热重载会产生伪耗时）
 * - 24h 去重（localStorage）：慢设备（大库）不该每次启动都被打扰
 *
 * 已知取舍：token 过期重新登录的会话，耗时含用户输入密码时间，可能误报；
 * 该场景罕见且受 24h 去重约束，可接受。
 */

import type { StartupSyncMode } from './startupSyncText'

const SLOW_THRESHOLD_MS = 8000
const SHOW_DELAY_MS = 1500
const DEDUPE_KEY = 'startup_slow_hint_last_shown'
const DEDUPE_INTERVAL_MS = 24 * 60 * 60 * 1000

/** 一次性标记（先到先得）：Suji/CRDT 两页面常驻保活并行挂载，首个就绪者记录 */
let marked = false
/** 本会话启动同步模式（full=全量下载 / incremental=增量同步；null=尚未判定） */
let startupSyncMode: StartupSyncMode | null = null
/** markAppReady 后锁定：先到先得时刻的模式快照，此后标记写入一律忽略 */
let modeLocked = false
let listeners: Array<() => void> = []

/**
 * 标记本会话启动同步模式（SujiLoadingSkeleton 判定后调用）
 *
 * 判定依据本地水合回调 + HWM 基线（均早于任何下载数据到达），在下载开始前
 * 捕获——下载中 HWM 会被推进，事后判定无法区分"刚全量下完"与"本有缓存"。
 * 水合回调本地即达，必然早于云端对齐（up-to-date 需网络往返），markAppReady
 * 读取到的一定是已判定值；极端竞态下 null 保守不提示。
 * user 就绪前后判定 effect 会重跑，markAppReady 前允许覆盖修正。
 */
export function markStartupSyncMode(mode: StartupSyncMode): void {
  if (modeLocked) return
  startupSyncMode = mode
}

function shouldShow(elapsedMs: number, aligned: boolean): boolean {
  if (import.meta.env.DEV) return false
  if (elapsedMs <= SLOW_THRESHOLD_MS) return false
  if (!aligned) return false
  // ★ 全量下载（新装/换设备/手动全量重下）耗时长属预期，不提示；
  //   null（尚未判定，理论竞态）保守不提示——宁可漏提示不误打扰
  if (startupSyncMode !== 'incremental') return false
  try {
    const last = Number(localStorage.getItem(DEDUPE_KEY) || 0)
    if (Date.now() - last < DEDUPE_INTERVAL_MS) return false
  } catch {
    // localStorage 不可用时照常提示
  }
  return true
}

/**
 * 应用就绪标记（页面骨架屏退出时调用，每会话仅首次生效）
 * @param aligned 就绪时云端是否已对齐（false = 离线只读兜底放行，不提示）
 */
export function markAppReady(aligned: boolean): void {
  if (marked) return
  marked = true
  modeLocked = true
  // performance.now() 相对 timeOrigin（webview 导航起点）——用户感知的完整启动时长
  const elapsedMs = performance.now()
  console.log('[Startup] 应用就绪（慢启动检测）:', Math.round(elapsedMs), 'ms, aligned:', aligned, ', syncMode:', startupSyncMode)
  if (!shouldShow(elapsedMs, aligned)) return
  // 延迟弹出：避免打断编辑器首帧渲染与启动聚焦
  window.setTimeout(() => {
    try {
      localStorage.setItem(DEDUPE_KEY, String(Date.now()))
    } catch {
      // 忽略写入失败（下次可能重复提示，无害）
    }
    listeners.forEach((fn) => fn())
  }, SHOW_DELAY_MS)
}

/** 订阅慢启动提示（全局弹窗组件挂载时订阅一次） */
export function subscribeSlowHint(fn: () => void): () => void {
  listeners.push(fn)
  return () => {
    listeners = listeners.filter((l) => l !== fn)
  }
}
